import React, { useRef, useCallback } from 'react';
import type { SupportedLanguage } from './snippet.types';
import '../../styles/features/snippet/SnippetEditor.css';

interface Props {
  code: string;
  onChange: (code: string) => void;
  language: SupportedLanguage;
}

const SnippetEditor: React.FC<Props> = ({ code, onChange }) => {
  const linesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lineCount = code.split('\n').length;

  const handleScroll = useCallback(() => {
    if (textareaRef.current && linesRef.current) {
      linesRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Tab key inserts 2 spaces instead of losing focus
      if (e.key === 'Tab') {
        e.preventDefault();
        const textarea = e.currentTarget;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = code.substring(0, start) + '  ' + code.substring(end);
        onChange(newValue);
        // Restore cursor position after state update
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        });
      }
    },
    [code, onChange],
  );

  return (
    <div className="snippet-editor">
      <div ref={linesRef} className="snippet-editor__lines" aria-hidden="true">
        {Array.from({ length: lineCount }, (_, i) => (
          <span key={i + 1} className="snippet-editor__line-num">
            {i + 1}
          </span>
        ))}
      </div>
      <textarea
        ref={textareaRef}
        className="snippet-editor__textarea"
        value={code}
        onChange={handleChange}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        aria-label="Code editor"
        placeholder="// Paste your code here..."
      />
    </div>
  );
};

export default SnippetEditor;
